// src/pages/TutorDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/config';
import '../styles/TutorDashboard.css';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  Timestamp
} from 'firebase/firestore';
import GroupDetails from '../features/tutor/GroupDetails';
import {
  DAYS,
  buildGroupName,
  cleanSessions,
  getSessionStartMinutes,
  toHHMM
} from '../utils/groupSchedule';

// Two session slots: Session 1 (required) and Session 2 (optional).
const EMPTY_SESSIONS = [{ day: 'Monday', time: '' }, { day: '', time: '' }];

// Validate Google Meet URL
function isValidMeetLink(url) {
  try {
    const parsed = new URL(url);
    return parsed.hostname === 'meet.google.com' && parsed.pathname.length > 1;
  } catch {
    return false;
  }
}

function TutorDashboard() {
  const { currentUser, logout } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  // Schedule modal — used both to create a group and to edit an existing
  // group's days & times. editingScheduleGroup is null when creating.
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [editingScheduleGroup, setEditingScheduleGroup] = useState(null);
  const [newSessions, setNewSessions] = useState(EMPTY_SESSIONS);
  const [scheduleError, setScheduleError] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [message, setMessage] = useState(null);

  // Manage modal
  const [managingGroup, setManagingGroup] = useState(null);
  const [assignedLearners, setAssignedLearners] = useState([]);

  // Meet link edit state: { groupId, value, error, saving }
  const [editingMeet, setEditingMeet] = useState(null);
  const meetInputRef = useRef(null);

  useEffect(() => {
    if (editingMeet && meetInputRef.current) {
      meetInputRef.current.focus();
    }
  }, [editingMeet?.groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    try { await logout(); } catch (error) { console.error('Logout error:', error); }
  };

  // ── Fetch groups ──────────────────────────────────────────────────────────
  const fetchGroups = async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      const groupsSnapshot = await getDocs(query(
        collection(db, 'tutor_groups'),
        where('tutor_id', '==', currentUser.uid)
      ));

      const groupsData = await Promise.all(
        groupsSnapshot.docs.map(async (groupDoc) => {
          const group = { id: groupDoc.id, ...groupDoc.data() };
          const learners = await Promise.all(
            (group.learner_ids || []).map(async (learnerId) => {
              const userDoc = await getDoc(doc(db, 'users', learnerId));
              if (userDoc.exists()) {
                const data = userDoc.data();
                return { id: learnerId, name: data.displayName || data.name || data.email || 'Unknown' };
              }
              return { id: learnerId, name: 'Unknown' };
            })
          );
          return { ...group, learners };
        })
      );

      groupsData.sort((a, b) => a.name.localeCompare(b.name));
      setGroups(groupsData);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setMessage({ type: 'error', text: 'Failed to load groups.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroups(); }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Create / edit group schedule ──────────────────────────────────────────
  // The group's title is always built from its days & times, so the two can
  // never disagree (e.g. "Wednesday & Saturday 6:00 PM").
  const openCreateGroup = () => {
    setEditingScheduleGroup(null);
    setNewSessions(EMPTY_SESSIONS);
    setScheduleError('');
    setShowCreateGroup(true);
  };

  const openEditSchedule = (group) => {
    // Pre-fill from the saved sessions. Older groups often have an empty
    // `time` with the time only in their title — read it from there so the
    // tutor or admin just has to check it and press Save.
    const prefilled = (group.sessions || []).slice(0, 2).map(s => ({
      day: s.day || '',
      time: toHHMM(getSessionStartMinutes(s, group.name))
    }));
    while (prefilled.length < 2) prefilled.push({ day: '', time: '' });
    setEditingScheduleGroup(group);
    setNewSessions(prefilled);
    setScheduleError('');
    setShowCreateGroup(true);
  };

  const closeScheduleModal = () => {
    setShowCreateGroup(false);
    setEditingScheduleGroup(null);
    setNewSessions(EMPTY_SESSIONS);
    setScheduleError('');
  };

  const updateSessionSlot = (i, field, value) => {
    const updated = [...newSessions];
    updated[i] = { ...updated[i], [field]: value };
    setNewSessions(updated);
    setScheduleError('');
  };

  const handleSaveSchedule = async (e) => {
    e.preventDefault();
    const [first, second] = newSessions;

    if (!first.day) { setScheduleError('Please choose a day for Session 1.'); return; }
    if (!first.time) { setScheduleError('Please set a start time for Session 1.'); return; }
    if (second.day && !second.time) { setScheduleError('Please set a start time for Session 2.'); return; }
    if (second.day && second.day === first.day) { setScheduleError('The two sessions must be on different days.'); return; }

    const sessions = cleanSessions(newSessions.filter(s => s.day));
    const name = buildGroupName(sessions);

    setSavingSchedule(true);
    try {
      if (editingScheduleGroup) {
        await updateDoc(doc(db, 'tutor_groups', editingScheduleGroup.id), {
          name,
          sessions,
          updated_at: Timestamp.now()
        });
        setMessage({ type: 'success', text: 'Schedule updated.' });
      } else {
        await addDoc(collection(db, 'tutor_groups'), {
          tutor_id: currentUser.uid,
          name,
          sessions,
          learner_ids: [],
          created_at: Timestamp.now()
        });
        setMessage({ type: 'success', text: 'Group created!' });
      }
      closeScheduleModal();
      await fetchGroups();
    } catch (error) {
      console.error('Error saving group schedule:', error);
      setScheduleError('Failed to save. Please try again.');
    } finally {
      setSavingSchedule(false);
    }
  };

  // ── Delete group ──────────────────────────────────────────────────────────
  const handleDeleteGroup = async (group, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${group.name}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'tutor_groups', group.id));
      setMessage({ type: 'success', text: 'Group deleted.' });
      await fetchGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      setMessage({ type: 'error', text: 'Failed to delete group.' });
    }
  };

  // ── Meet link ─────────────────────────────────────────────────────────────
  const handleMeetEditStart = (group, e) => {
    e.stopPropagation();
    setEditingMeet({ groupId: group.id, value: group.meetLink || '', error: '', saving: false });
  };

  const handleMeetSave = async (e) => {
    e?.stopPropagation();
    if (!editingMeet) return;
    const trimmed = editingMeet.value.trim();

    if (trimmed !== '' && !isValidMeetLink(trimmed)) {
      setEditingMeet(prev => ({ ...prev, error: 'Enter a valid meet.google.com link' }));
      return;
    }

    setEditingMeet(prev => ({ ...prev, saving: true, error: '' }));
    try {
      await updateDoc(doc(db, 'tutor_groups', editingMeet.groupId), {
        meetLink: trimmed,
        updated_at: Timestamp.now()
      });
      // Update local state immediately — no full refetch needed
      setGroups(prev => prev.map(g =>
        g.id === editingMeet.groupId ? { ...g, meetLink: trimmed } : g
      ));
      setEditingMeet(null);
    } catch (err) {
      console.error('Error saving Meet link:', err);
      setEditingMeet(prev => ({ ...prev, saving: false, error: 'Failed to save. Try again.' }));
    }
  };

  const handleMeetCancel = (e) => {
    e?.stopPropagation();
    setEditingMeet(null);
  };

  const handleMeetKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') handleMeetSave();
    if (e.key === 'Escape') handleMeetCancel();
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ── Fetch assigned learners (for Manage modal) ────────────────────────────
  const fetchAssignedLearners = async () => {
    try {
      const assignmentsSnap = await getDocs(query(
        collection(db, 'tutoring_assignments'),
        where('tutor_id', '==', currentUser.uid)
      ));
      const learners = await Promise.all(
        assignmentsSnap.docs.map(async (d) => {
          const learnerId = d.data().learner_id;
          const userDoc = await getDoc(doc(db, 'users', learnerId));
          if (userDoc.exists()) {
            const data = userDoc.data();
            return { id: learnerId, name: data.displayName || data.name || data.email || 'Unknown' };
          }
          return { id: learnerId, name: 'Unknown' };
        })
      );
      learners.sort((a, b) => a.name.localeCompare(b.name));
      setAssignedLearners(learners);
    } catch (error) {
      console.error('Error fetching assigned learners:', error);
    }
  };

  // ── Toggle learner in/out of managed group ────────────────────────────────
  const handleToggleLearner = async (learnerId) => {
    const currentIds = managingGroup.learner_ids || [];
    const isInGroup = currentIds.includes(learnerId);
    if (!isInGroup && currentIds.length >= 3) {
      setMessage({ type: 'error', text: 'A group can have at most 3 learners.' });
      return;
    }
    try {
      await updateDoc(doc(db, 'tutor_groups', managingGroup.id), {
        learner_ids: isInGroup ? arrayRemove(learnerId) : arrayUnion(learnerId)
      });
      const updatedDoc = await getDoc(doc(db, 'tutor_groups', managingGroup.id));
      setManagingGroup({ id: updatedDoc.id, ...updatedDoc.data() });
      await fetchGroups();
    } catch (error) {
      console.error('Error updating group learners:', error);
      setMessage({ type: 'error', text: 'Failed to update group.' });
    }
  };

  // ── Derived: which learner IDs are already in ANY other group ─────────────
  const learnersInOtherGroups = managingGroup
    ? groups
        .filter(g => g.id !== managingGroup.id)
        .flatMap(g => g.learner_ids || [])
    : [];

  // ─────────────────────────────────────────────────────────────────────────

  if (loading) return <div className="tutor-dashboard-loading">Loading...</div>;

  if (selectedGroup) {
    return (
      <GroupDetails
        group={selectedGroup}
        onBack={() => setSelectedGroup(null)}
      />
    );
  }

  return (
    <div className="tutor-dashboard">
      <div className="tutor-header">
        <h1>My Groups</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button className="add-learner-button" onClick={openCreateGroup}>
            + New Group
          </button>
          <button
            onClick={handleLogout}
            style={{ padding: '8px 16px', backgroundColor: '#e53935', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
          >
            Log Out
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {/* Create Group / Edit Schedule Modal */}
      {showCreateGroup && (
        <div className="add-learner-modal">
          <div className="modal-content">
            <h2>{editingScheduleGroup ? 'Edit days & times' : 'Create Group'}</h2>
            <p style={{ color: '#6c757d', fontSize: '14px', marginBottom: '16px' }}>
              Start times are in India time. Each session lasts 30 minutes.
            </p>
            <form onSubmit={handleSaveSchedule}>
              {[0, 1].map(i => (
                <div key={i} className="session-slot">
                  <div className="session-slot-label">
                    {i === 0 ? 'Session 1' : 'Session 2 (optional)'}
                  </div>
                  <div className="session-slot-fields">
                    <select
                      value={newSessions[i].day}
                      onChange={e => updateSessionSlot(i, 'day', e.target.value)}
                      className="group-day-select"
                      autoFocus={i === 0}
                    >
                      <option value="">{i === 0 ? '— choose a day —' : '— no second session —'}</option>
                      {DAYS.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <input
                      type="time"
                      value={newSessions[i].time}
                      onChange={e => updateSessionSlot(i, 'time', e.target.value)}
                      className="group-time-input"
                      disabled={i === 1 && !newSessions[1].day}
                    />
                  </div>
                </div>
              ))}

              {/* Live preview of the title that will be saved */}
              <div style={{ margin: '12px 0', fontSize: '14px', color: '#374151' }}>
                Title:{' '}
                <strong>{buildGroupName(newSessions.filter(s => s.day)) || '—'}</strong>
              </div>

              {scheduleError && (
                <div style={{ color: '#dc2626', fontSize: '14px', marginBottom: '12px' }}>
                  {scheduleError}
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="submit-button" disabled={savingSchedule}>
                  {savingSchedule ? 'Saving…' : editingScheduleGroup ? 'Save' : 'Create'}
                </button>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={closeScheduleModal}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Learners Modal */}
      {managingGroup && (
        <div className="add-learner-modal">
          <div className="modal-content">
            <h2>Manage — {managingGroup.name}</h2>
            <p style={{ color: '#6c757d', fontSize: '14px', marginBottom: '16px' }}>
              Select learners for this group (max 3). Learners already in another group are not shown.
            </p>

            {assignedLearners.filter(l => !learnersInOtherGroups.includes(l.id) || (managingGroup.learner_ids || []).includes(l.id)).length === 0 ? (
              <p style={{ color: '#6c757d' }}>No available learners. All assigned learners are already in other groups.</p>
            ) : (
              <div className="manage-learners-list">
                {assignedLearners
                  .filter(l =>
                    (managingGroup.learner_ids || []).includes(l.id) ||
                    !learnersInOtherGroups.includes(l.id)
                  )
                  .map(learner => {
                    const inGroup = (managingGroup.learner_ids || []).includes(learner.id);
                    const groupFull = (managingGroup.learner_ids || []).length >= 3;
                    const disabled = !inGroup && groupFull;

                    return (
                      <div
                        key={learner.id}
                        className={`manage-learner-row ${inGroup ? 'in-group' : ''} ${disabled ? 'disabled' : ''}`}
                        onClick={() => !disabled && handleToggleLearner(learner.id)}
                      >
                        <span className="manage-learner-name">{learner.name}</span>
                        <input
                          type="checkbox"
                          checked={inGroup}
                          disabled={disabled}
                          onChange={() => !disabled && handleToggleLearner(learner.id)}
                          style={{ width: '18px', height: '18px', cursor: disabled ? 'not-allowed' : 'pointer' }}
                        />
                      </div>
                    );
                  })}
              </div>
            )}

            {(managingGroup.learner_ids || []).length >= 3 && (
              <p style={{ color: '#6c757d', fontSize: '13px', marginTop: '10px' }}>
                Maximum 3 learners reached. Remove one to add another.
              </p>
            )}

            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button className="cancel-button" onClick={() => setManagingGroup(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Groups list */}
      {groups.length === 0 ? (
        <div className="empty-state">
          <p>No groups yet.</p>
          <p>Click &quot;+ New Group&quot; to create your first group.</p>
        </div>
      ) : (
        <div className="groups-list">
          {groups.map(group => {
            const isMeetEditing = editingMeet?.groupId === group.id;

            return (
              <div key={group.id} className="group-card">
                <div
                  className="group-card-main"
                  onClick={() => {
                    if (isMeetEditing) return;
                    setSelectedGroup(group);
                  }}
                >
                  <div className="group-info">

                    {/* Group title — built from days & times; the ✏️ edits those */}
                    <div className="group-name-row">
                      <h3>{group.name}</h3>
                      <button
                        className="edit-name-button"
                        title="Edit days & times"
                        onClick={e => {
                          e.stopPropagation();
                          openEditSchedule(group);
                        }}
                      >
                        ✏️
                      </button>
                    </div>

                    <div className="group-learners-preview">
                      {group.learners.length === 0
                        ? <span className="no-learners">No learners yet</span>
                        : group.learners.map(l => (
                            <span key={l.id} className="learner-chip">{l.name}</span>
                          ))
                      }
                    </div>

                    {/* ── Meet link area ── */}
                    <div className="meet-link-row" onClick={e => e.stopPropagation()}>
                      {isMeetEditing ? (
                        <>
                          <div className="meet-link-editor">
                            <svg className="meet-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15 8.5V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-2.5l4 3v-13l-4 3Z" fill="#00897B"/>
                            </svg>
                            <input
                              ref={meetInputRef}
                              className="meet-link-input"
                              type="url"
                              value={editingMeet.value}
                              onChange={e => setEditingMeet(prev => ({ ...prev, value: e.target.value, error: '' }))}
                              onKeyDown={handleMeetKeyDown}
                              placeholder="https://meet.google.com/abc-defg-hij"
                            />
                            <button
                              className="meet-link-save-btn"
                              onClick={handleMeetSave}
                              disabled={editingMeet.saving}
                              title="Save"
                            >
                              {editingMeet.saving ? '…' : '✓'}
                            </button>
                            <button
                              className="meet-link-cancel-btn"
                              onClick={handleMeetCancel}
                              title="Cancel"
                            >
                              ✕
                            </button>
                          </div>
                          {editingMeet.error && (
                            <span className="meet-link-error">{editingMeet.error}</span>
                          )}
                        </>
                      ) : group.meetLink ? (
                        <div className="meet-link-display">
                          <a
                            href={group.meetLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="meet-join-btn"
                            title={group.meetLink}
                            onClick={e => e.stopPropagation()}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15 8.5V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-2.5l4 3v-13l-4 3Z" fill="white"/>
                            </svg>
                            Join Meet
                          </a>
                          <button
                            className="meet-link-edit-icon"
                            onClick={e => handleMeetEditStart(group, e)}
                            title="Edit Meet link"
                          >
                            ✏️
                          </button>
                        </div>
                      ) : (
                        <button
                          className="meet-link-add-btn"
                          onClick={e => handleMeetEditStart(group, e)}
                        >
                          + Add Meet link
                        </button>
                      )}
                    </div>
                    {/* ───────────────── */}

                  </div>
                  <div className="arrow">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 3L19 12L5 21V3Z" fill="#19a4a0"/>
                    </svg>
                  </div>
                </div>

                {/* Card footer: Manage + Delete */}
                <div className="group-card-footer">
                  <button
                    className="manage-group-button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      setManagingGroup(group);
                      await fetchAssignedLearners();
                    }}
                  >
                    Manage
                  </button>
                  <button
                    className="delete-group-button"
                    onClick={(e) => handleDeleteGroup(group, e)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TutorDashboard;